// Shared helpers for Attendance → Location Tracking (API, formatting, map icons).
import L from "leaflet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/attendance/location-tracking`;

export class ApiError extends Error {
  constructor(message, status) { super(message); this.status = status; }
}

export async function ltApi(path, { method = "GET", body, businessId, params } = {}) {
  const qs = new URLSearchParams();
  if (businessId) qs.set("business_id", businessId);
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, v); });
  const url = `${BASE}${path}${qs.toString() ? `?${qs}` : ""}`;
  const res = await fetch(url, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.detail || `Request failed (${res.status})`, res.status);
  return data;
}
export const ltUrl = (path, { businessId, params } = {}) => {
  const qs = new URLSearchParams();
  if (businessId) qs.set("business_id", businessId);
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, v); });
  return `${BASE}${path}${qs.toString() ? `?${qs}` : ""}`;
};

const IST = { timeZone: "Asia/Kolkata" };
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { ...IST, hour: "2-digit", minute: "2-digit", hour12: true }) : "—");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { ...IST, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "—");
export const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtHours = (h) => {
  if (h === null || h === undefined || Number.isNaN(Number(h))) return "—";
  const mins = Math.round(Number(h) * 60);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}h ${String(mins % 60).padStart(2, "0")}m`;
};
export const fmtDistance = (m) => (m === null || m === undefined ? "—" : m >= 1000 ? `${(m / 1000).toFixed(m >= 10000 ? 0 : 1)} km` : `${Math.round(m)} m`);
export const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};
export const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

// IST calendar dates for the period presets.
export const istToday = () => new Date().toLocaleDateString("en-CA", IST);
export function presetRange(preset) {
  const today = istToday();
  const d = new Date(`${today}T00:00:00`);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  if (preset === "yesterday") { d.setDate(d.getDate() - 1); return { from: iso(d), to: iso(d) }; }
  if (preset === "week") { const s = new Date(d); s.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return { from: iso(s), to: today }; }
  if (preset === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  return { from: today, to: today };
}

export const STATE_META = {
  WORKING: { label: "Working", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "#16a34a" },
  PUNCHED_OUT: { label: "Punched Out", chip: "bg-slate-100 text-slate-700 ring-slate-200", dot: "#64748b" },
  ON_LEAVE: { label: "On Leave", chip: "bg-violet-50 text-violet-700 ring-violet-200", dot: "#7c3aed" },
  ABSENT: { label: "Absent", chip: "bg-rose-50 text-rose-700 ring-rose-200", dot: "#e11d48" },
  NOT_PUNCHED_IN: { label: "Not Punched In", chip: "bg-amber-50 text-amber-700 ring-amber-200", dot: "#d97706" },
};
export const TRACKING_META = {
  active: { label: "Tracking active", cls: "text-emerald-700" },
  stale: { label: "No recent update", cls: "text-amber-700" },
  stopped: { label: "Tracking stopped", cls: "text-slate-500" },
  web_punch: { label: "Punched in from web", cls: "text-slate-500" },
  none: { label: "—", cls: "text-slate-400" },
};

export function StateChip({ state }) {
  const m = STATE_META[state] || STATE_META.NOT_PUNCHED_IN;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${m.chip}`}>{m.label}</span>;
}

// Marker icons are HTML (no image assets → no bundler path issues).
export function personIcon(name, color, { pulse = false } = {}) {
  return L.divIcon({
    className: "",
    iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -16],
    html: `<div style="position:relative;width:34px;height:34px">${pulse ? `<span style="position:absolute;inset:-5px;border-radius:9999px;background:${color};opacity:.22"></span>` : ""}<div style="position:relative;width:34px;height:34px;border-radius:9999px;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font:600 12px/1 system-ui,sans-serif;border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,.35)">${initials(name).replace(/[<>&]/g, "")}</div></div>`,
  });
}
export function endpointIcon(label, color) {
  return L.divIcon({
    className: "",
    iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -12],
    html: `<div style="width:26px;height:26px;border-radius:9999px;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font:700 11px/1 system-ui,sans-serif;border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,.35)">${label}</div>`,
  });
}
export const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
export const DEFAULT_CENTER = [22.9734, 78.6569]; // India
